import { isValid } from 'date-fns'
import { isAfter } from 'date-fns'
import { subDays } from 'date-fns'
import { compatibleDate } from '~/libs/date-helpers'
import { BLACKLISTED_AUTOSUB_TYPES } from '~/libs/deal-status'

export const ELIGIBLE_OFFER_TYPES = {
  TIER_2: 'tier-2',
  TIER_3: 'tier-3',
  ALTERNATIVE_PRODUCT: 'alternative-product',
  CREDIT_CARD: 'credit-card',
}

export const ELIGIBLE_OFFER_CATEGORY = {
  TIER_TWO: 'tierTwo',
  TIER_THREE: 'tierThree',
  CREDIT_CARD: 'creditCard',
}

export const ELIGIBLE_OFFER_CATEGORY_BY_TYPE = {
  [ELIGIBLE_OFFER_TYPES.TIER_2]: ELIGIBLE_OFFER_CATEGORY.TIER_TWO,
  [ELIGIBLE_OFFER_TYPES.TIER_3]: ELIGIBLE_OFFER_CATEGORY.TIER_THREE,
  [ELIGIBLE_OFFER_TYPES.CREDIT_CARD]: ELIGIBLE_OFFER_CATEGORY.CREDIT_CARD,
  [ELIGIBLE_OFFER_TYPES.ALTERNATIVE_PRODUCT]: ELIGIBLE_OFFER_CATEGORY.TIER_THREE,
}

const DISMISSAL_PERIOD_DAYS = 120
const TIER_ONE_ELIGIBILITY_EXPIRATION = 30 //days

/**
 * Detect if specific type of offer is available to user
 * @param latestOfferDismissal
 * @param marketplaceAppCompleted
 * @returns {{isOfferAvailable(string): boolean}}
 */
function dismissalChecker (latestOfferDismissal, marketplaceAppCompleted) {
  const checkCategory = (offerCategory, strict = false) => {
    const dismissalCategory = latestOfferDismissal?.offerType ?? ''
    if (strict === false) {
      const disabled = (dismissalCategory === 'offer' && offerCategory === ELIGIBLE_OFFER_CATEGORY.TIER_TWO) ||
        (dismissalCategory === 'alternateProduct' && offerCategory === ELIGIBLE_OFFER_CATEGORY.TIER_THREE);

      return !disabled;
    }

    switch (offerCategory) {
      case ELIGIBLE_OFFER_CATEGORY.TIER_TWO:
        return false;
      case ELIGIBLE_OFFER_CATEGORY.TIER_THREE:
        return !dismissalCategory || dismissalCategory === 'offer';
      case ELIGIBLE_OFFER_CATEGORY.CREDIT_CARD:
        return true;
      default:
        return false;
    }
  }

  const marketplaceCompletedDate = isValid(new Date(compatibleDate(marketplaceAppCompleted)))
    ? new Date(compatibleDate(marketplaceAppCompleted))
    : null
  const dismissalDate = isValid(new Date(compatibleDate(latestOfferDismissal?.created)))
    ? new Date(compatibleDate(latestOfferDismissal?.created))
    : null

  return {
    /**
     * @param {string} offerCategory
     * @returns {boolean}
     */
    isOfferAvailable (offerCategory) {
      if (!dismissalDate || !marketplaceCompletedDate) return true;

      if (isAfter(marketplaceCompletedDate, dismissalDate)) {
        return checkCategory(offerCategory, false)
      }

      const allowedDate = subDays(new Date(), DISMISSAL_PERIOD_DAYS)
      if (isAfter(allowedDate, dismissalDate)) {
        return checkCategory(offerCategory, true)
      }

      return false
    }
  }
}

/**
 * Determine if qualified borrower is eligible for another tier offers.
 * @param {[Object] | null} deals
 * @param {string | null} marketplaceAppCompleted
 * @returns {boolean}
 */
function isEligibleForAnotherOffers (deals, marketplaceAppCompleted) {
  const tierOneEligibilityExpirationDate = subDays(new Date(), TIER_ONE_ELIGIBILITY_EXPIRATION)

  const activeTierOneDeals = (deals || []).filter((deal) => {
    return ['optin', 'ppp'].includes(deal.autoSubmitType) === false &&
      deal.loanProductOfferType === 'tier-1' &&
      deal.stage !== 'inactive' &&
      ['declined', 'offerDeclined'].includes(deal.status) === false
  })

  if (Boolean(activeTierOneDeals.length)) {
    return false
  }

  const inactiveTierOneDeals = (deals || []).filter((deal) => {
    return (!deal.autoSubmitType || !BLACKLISTED_AUTOSUB_TYPES.includes(deal.autoSubmitType)) &&
      deal.loanProductOfferType === 'tier-1' &&
      (deal.stage === 'inactive' || ['declined', 'offerDeclined'].includes(deal.status))
  })

  const recentInactiveDeal = inactiveTierOneDeals.length ? inactiveTierOneDeals.at(0) : null

  if (!recentInactiveDeal) {
    const marketplaceCompletedDate = isValid(new Date(compatibleDate(marketplaceAppCompleted)))
      ? new Date(compatibleDate(marketplaceAppCompleted))
      : null

    return Boolean(marketplaceCompletedDate)
      && isAfter(tierOneEligibilityExpirationDate, marketplaceCompletedDate)
  }

  if (['declined', 'offerDeclined'].includes(recentInactiveDeal.status)) {
    return true;
  }

  const recentInactiveDealCreatedDate = isValid(new Date(compatibleDate(recentInactiveDeal.created)))
    ? new Date(compatibleDate(recentInactiveDeal.created))
    : null

  return recentInactiveDealCreatedDate
    && isAfter(tierOneEligibilityExpirationDate, recentInactiveDealCreatedDate)
}

/**
 * Get recent tier-2 deal
 * @param {[Object]} deals
 * @param {{isOfferAvailable(string): boolean}} dismissalHelper
 * @returns {null|Object}
 */
function getTierTwoDeal(deals, dismissalHelper) {
  if (dismissalHelper.isOfferAvailable(ELIGIBLE_OFFER_CATEGORY.TIER_TWO) === false) {
    return null
  }
  const tierTwoDeals = (deals || []).filter((deal) => {
    return deal.loanProductOfferType === 'tier-2' &&
      deal.stage !== 'inactive' &&
      ['declined', 'offerDeclined'].includes(deal.status) === false &&
      (deal.offers || []).some((offer) => Boolean(offer.offerLink))
  });

  if (!tierTwoDeals.length) {
    return null
  }
  return tierTwoDeals.at(0)
}

/**
 * Get prioritized tier-3 offer
 * @param {[Object] | null} alternateOptions
 * @param {{isOfferAvailable(string): boolean}} dismissalHelper
 * @returns {null|Object}
 */
function getTierThreeProduct(alternateOptions, dismissalHelper) {
  if (dismissalHelper.isOfferAvailable(ELIGIBLE_OFFER_CATEGORY.TIER_THREE) === false) {
    return null
  }
  const tierThreeProducts = (alternateOptions || []).filter(
    (opt) => opt.category === 'tier-3'
  )
  return tierThreeProducts.length ? tierThreeProducts.at(0) : null
}

/**
 * Get prioritized credit card offer
 * @param {[Object] | null} creditCardOffers
 * @param {{isOfferAvailable(string): boolean}} dismissalHelper
 * @param {[Object] | null} creditCardDismissals
 * @returns {null|Object}
 */
function getCreditCardOffer(creditCardOffers, dismissalHelper, creditCardDismissals) {
  if (dismissalHelper.isOfferAvailable(ELIGIBLE_OFFER_CATEGORY.CREDIT_CARD) === false) {
    return null
  }
  if (!creditCardOffers?.length) {
    return null
  }

  const dismissedCCIds = (creditCardDismissals || [])
    .filter(dismissal => Boolean(dismissal.creditCardOptionId))
    .map(dismissal => dismissal.creditCardOptionId)

  const allowedOffers = creditCardOffers.filter(offer => !dismissedCCIds.includes(offer.id))
  if (!allowedOffers.length) {
    return null
  }

  return allowedOffers.at(0)
}


export function getEligibleOffer({
  deals,
  alternateOptions,
  creditCardOffers,
  latestOfferDismissal,
  creditCardDismissals,
  marketplaceAppCompleted,
  isQualified,
}) {
  const dismissalHelper = dismissalChecker(latestOfferDismissal, marketplaceAppCompleted)

  const eligibilityWaterfall = [
    {
      category: ELIGIBLE_OFFER_CATEGORY.TIER_TWO,
      get: getTierTwoDeal.bind(this, deals, dismissalHelper),
    },
    {
      category: ELIGIBLE_OFFER_CATEGORY.TIER_THREE,
      get: getTierThreeProduct.bind(this, alternateOptions, dismissalHelper)
    },
    {
      category: ELIGIBLE_OFFER_CATEGORY.CREDIT_CARD,
      get: getCreditCardOffer.bind(this, creditCardOffers, dismissalHelper, creditCardDismissals),
    },
  ]

  let offer = null
  let eligibilityCategory = null

  for (let item of eligibilityWaterfall) {
    offer = item.get()
    if (offer) {
      eligibilityCategory = item.category
      break;
    }
  }

  if (!eligibilityCategory) {
    return null
  }

  let isEligible = isQualified
    ? isEligibleForAnotherOffers(deals, marketplaceAppCompleted)
    : Boolean(marketplaceAppCompleted)

  if (!isEligible) {
    return null;
  }

  return {
    offer,
    category: eligibilityCategory,
  }
}
